import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSectionMeta } from "@/context/SiteConfigContext";
import { api, assetUrl } from "@/lib/api";
import { localizedSlug } from "@/lib/utils";
import { SectionHead, MaskWords, SectionButtons } from "@/components/site/primitives";
import { Reveal } from "@/components/site/Reveal";

export default function HomeWorks() {
  const { lang, t } = useLang();
  const c = t.home_works || {};
  const meta = useSectionMeta("home_works");
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data.slice(0, 3))).catch(() => setProjects([]));
  }, []);

  const g = (p, k) => p[`${k}_${lang}`] || p[`${k}_ro`] || "";
  if (!projects.length) return null;

  return (
    <section
      id="home_works"
      data-testid="home-works"
      className="bg-noir text-paper px-6 md:px-12 lg:px-20 py-28 md:py-40"
    >
      <SectionHead dark label={c.label} index="" />
      <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
        <MaskWords
          Tag="h2"
          text={c.title}
          className="col-span-12 lg:col-span-8 display uppercase text-[12vw] lg:text-[8vw] leading-[0.85] break-words"
        />
        <p className="col-span-12 lg:col-span-4 text-paper/60 leading-[1.6]">{c.intro}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {projects.map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.08}>
            <Link
              to={`/portfolio/${localizedSlug(p, lang)}`}
              data-testid={`home-work-${i}`}
              data-cursor="link"
              className="group block"
            >
              <div className="relative overflow-hidden aspect-video bg-white/5">
                {p.cover_image && (
                  <img
                    src={assetUrl(p.cover_image)}
                    alt={g(p, "title")}
                    className="w-full h-full object-contain opacity-85 transition-opacity duration-[1200ms] ease-out group-hover:opacity-100"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
              <div className="flex items-start justify-between gap-4 pt-4">
                <div>
                  <div className="eyebrow text-paper/45 mb-1">
                    {g(p, "category")} · {p.year}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl leading-none">{g(p, "title")}</h3>
                </div>
                <ArrowUpRight
                  size={24}
                  className="shrink-0 mt-1 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </div>
            </Link>
          </Reveal>
        ))}
      </div>

      <div className="mt-12 md:mt-16">
        <SectionButtons buttons={meta.buttons} dark />
      </div>
    </section>
  );
}
